import { useState } from 'react';
import type { ShopifyBriefingCard as Briefing } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { messageDir } from '../../../../lib/message-direction';
import { ENTITY_TYPE, useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { ShopifySourceLine } from '../../connectors/ShopifySourceLine';
import { Button, Card, Typography } from '../../ui';
import { CHAT_HOME_BI } from '../message-utils';
import styles from './ShopifyBriefingCard.module.css';

const TITLE_SEPARATOR = '; ';

/**
 * The store briefing under an assistant reply that carries `metadata.shopifyBriefingCard`. Every number is copied
 * from the saved study by the server. The chips send an ordinary owner message; the gap chips build theirs here so
 * they read in the owner's language.
 */
export function ShopifyBriefingCard({
  card,
  conversationId,
  disabled,
  onSend,
}: {
  card: Briefing;
  conversationId?: string;
  disabled: boolean;
  onSend?: (text: string) => void;
}) {
  const { t } = useTranslation();
  const seenRef = useItemSeenBi('shopify_briefing_card', {
    ...(conversationId ? { entity: { id: conversationId, type: ENTITY_TYPE.conversation } } : {}),
    additionalFields: { facts: String(card.facts.length), empty: String(card.facts.length === 0 && !card.lowStock) },
  });
  const { logPageClickBi } = useGenericBi();
  const [failedImage, setFailedImage] = useState<string>();
  const locked = disabled || !onSend;
  const { facts, lowStock, gaps, nextSteps, source } = card;
  const gapChips = gaps
    ? [
        { list: gaps.missingDescription, label: 'writeDescriptions', message: 'writeDescriptionsMessage' },
        { list: gaps.missingImage, label: 'showMissingImages', message: 'showMissingImagesMessage' },
      ].filter((chip) => chip.list.length > 0)
    : [];
  const send = (text: string, buttonName: string) => {
    logPageClickBi({ ...CHAT_HOME_BI, button_name: buttonName, ...(conversationId ? { conversation_id: conversationId } : {}) });
    onSend?.(text);
  };
  const stockDetail = lowStock
    ? t(lowStock.left > 0 ? 'chat.shopifyBriefing.lowStock.almostOut' : 'chat.shopifyBriefing.lowStock.soldOutDetail', { title: lowStock.title })
    : '';
  const showImage = lowStock?.imageUrl !== undefined && lowStock.imageUrl !== failedImage;

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={t('chat.shopifyBriefing.aria')} data-testid="shopify-briefing-card">
      <Typography variant="h4">
        {t('chat.shopifyBriefing.title', { store: source.storeName ?? source.storeDomain })}
      </Typography>
      {facts.length === 0 && !lowStock ? (
        <p className={styles.empty}>{t('chat.shopifyBriefing.empty')}</p>
      ) : (
        <ul className={styles.tiles}>
          {facts.map((fact, index) => (
            <Card component="li" variant="stone" size="sm" key={index} className={styles.tile}>
              <span className={styles.value} dir={messageDir(fact.value)}>
                {fact.unit ? `${fact.value} ${fact.unit}` : fact.value}
              </span>
              <span className={styles.label} dir={messageDir(fact.label)}>
                {fact.label}
              </span>
              {fact.comparison && (
                <span className={styles.comparison} dir={messageDir(fact.comparison)}>
                  {fact.comparison}
                </span>
              )}
            </Card>
          ))}
          {lowStock && (
            <Card component="li" variant="stone" size="sm" className={`${styles.tile} ${styles.stockTile}`}>
              <span>
                <span className={styles.value}>
                  {lowStock.left > 0 ? t('chat.shopifyBriefing.lowStock.left', { count: lowStock.left }) : t('chat.shopifyBriefing.lowStock.soldOut')}
                </span>
                <span className={styles.label} dir={messageDir(stockDetail)}>
                  {stockDetail}
                </span>
              </span>
              {showImage && (
                <img
                  className={styles.thumb}
                  src={lowStock.imageUrl}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => setFailedImage(lowStock.imageUrl)}
                />
              )}
            </Card>
          )}
        </ul>
      )}
      {gaps && gapChips.length > 0 && (
        <div className={styles.gaps}>
          <p className={styles.gapText}>{t('chat.shopifyBriefing.gaps.title', { checked: gaps.checked })}</p>
          <ul className={styles.gapCounts}>
            {gaps.missingDescription.length > 0 && <li>{t('chat.shopifyBriefing.gaps.noDescription', { count: gaps.missingDescription.length })}</li>}
            {gaps.missingImage.length > 0 && <li>{t('chat.shopifyBriefing.gaps.noImage', { count: gaps.missingImage.length })}</li>}
          </ul>
          <div className={styles.chips}>
            {gapChips.map((chip) => (
              <Button
                key={chip.label}
                variant="outlined"
                size="small"
                disabled={locked}
                onClick={() =>
                  send(
                    t(`chat.shopifyBriefing.gaps.${chip.message}`, { titles: chip.list.map((product) => product.title).join(TITLE_SEPARATOR) }),
                    `shopify-briefing-${chip.label}`,
                  )
                }
              >
                {t(`chat.shopifyBriefing.gaps.${chip.label}`)}
              </Button>
            ))}
          </div>
        </div>
      )}
      {nextSteps.length > 0 && (
        <div className={styles.steps}>
          <p className={styles.prompt}>{t('chat.shopifyBriefing.nextSteps.prompt')}</p>
          <div className={styles.chips}>
            {nextSteps.map((step, index) => (
              <Button key={index} variant="outlined" size="small" disabled={locked} onClick={() => send(step.message, 'shopify-briefing-next-step')}>
                <span dir={messageDir(step.label)}>{step.label}</span>
              </Button>
            ))}
          </div>
        </div>
      )}
      <ShopifySourceLine
        source={{
          provider: 'shopify',
          storeDomain: source.storeDomain,
          storeName: source.storeName,
          readAt: source.readAt,
          status: source.coverage ? 'partial' : 'complete',
          coverage: source.coverage,
        }}
      />
    </Card>
  );
}
